import { useEffect, useState, useRef } from "react";
import { resetPin, updatePin } from "../../../../../config/Services";
import { useSnackbar } from "../../hooks/SnackbarProvider";
import "./forgot-pin.css";
import { useNavigate } from "react-router-dom";

export const ForgotPin = ({ forgotPin, setForgotPin }) => {
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const [pin, setPin] = useState("");
  const [cPin, setCPin] = useState("");
  const [resetPinLoader, setResetPinLoader] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSend, setOtpSend] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [mob, setMob] = useState("");
  const [timer, setTimer] = useState(0);

  // Refs for individual OTP digit inputs
  const otpRefs = useRef([]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // Auto-verify OTP when all 6 digits entered (simulate: any 6 digits = verified)
  useEffect(() => {
    if (otp?.length === 6) {
      setOtpVerified(true);
    } else {
      setOtpVerified(false);
    }
  }, [otp]);

  const handleResendOtp = () => {
    if (timer > 0) return;
    showSnackbar("OTP sent successfully", "success");
    setTimer(30);
    resetPinFn(true);
  };

  const resetPinFn = (isResend = false) => {
    if (mob?.length === 10) {
      const payload = { mobile: mob };
      if (!loading) {
        setLoading(true);
        resetPin(payload)
          .then((res) => {
            if (res?.data?.data?.status === "ERROR" || res?.data?.status === "ERROR") {
              showSnackbar(
                res?.data?.data?.message || res?.data?.message || "User not found",
                "error"
              );
              setLoading(false);
              return;
            }
            setOtpSend(true);
            setTimer(30);
            if (!isResend) showSnackbar("OTP sent successfully", "success");
            setLoading(false);
          })
          .catch((err) => {
            showSnackbar(err?.response?.data?.message || err?.message, "error");
            setLoading(false);
          });
      }
    } else {
      showSnackbar("Invalid mobile number", "error");
      setLoading(false);
    }
  };

  const saveResetPin = () => {
    setResetPinLoader(true);
    if (cPin.length < 4 || pin.length < 4) {
      showSnackbar("PIN should be at least 4 characters", "error");
      setResetPinLoader(false);
      return;
    }
    const updatePayload = { mobile: mob, primaryPassword: cPin, otp };
    if (Number(cPin) === Number(pin)) {
      updatePin(updatePayload)
        .then((response) => {
          showSnackbar(
            "PIN changed successfully. You are getting redirected to login page.",
            "success"
          );
          setResetPinLoader(false);
          // setTimeout(() => {
          //   window.location.reload();
          // }, 700);
          setForgotPin(false);
        })
        .catch((err) => {
          showSnackbar(err?.response?.data?.message || err?.message, "error");
          setResetPinLoader(false);
        });
    } else {
      showSnackbar("PIN doesn't match. Please enter the correct PIN", "error");
      setResetPinLoader(false);
    }
  };

  const verifyMobile = (e) => {
    if (e?.key === "Enter") {
      e.preventDefault();
      resetPinFn();
    }
  };

  // OTP digit input handlers
  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const otpArr = otp?.split("");
    otpArr[index] = value;
    const newOtp = otpArr?.join("")?.slice(0, 6);
    setOtp(newOtp?.padEnd(6, "")?.trimEnd());

    // Build correctly — fill gaps
    const digits = otp?.split("");
    digits[index] = value;
    const joined = digits?.slice(0, 6)?.join("");
    setOtp(joined);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e?.key === "Backspace") {
      const digits = otp?.split("");
      if (digits[index]) {
        digits[index] = "";
        setOtp(digits?.join(""));
      } else if (index > 0) {
        otpRefs.current[index - 1]?.focus();
        const d = otp.split("");
        d[index - 1] = "";
        setOtp(d.join(""));
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    setOtp(pasted);
    const lastIdx = Math.min(pasted?.length, 5);
    otpRefs.current[lastIdx]?.focus();
  };

  const isResetEnabled = otpVerified && pin?.length === 4 && cPin?.length === 4;

  const handleNumber = (e) => {
    const val = e?.target?.value?.replace(/\D/g, "");
    if (val?.length > 10) {
      return;
    }
    setMob(val);
  };

  return (
    <div className="fp-root">
      {/* ── Mobile number row ── */}
      <div className="fp-field-group">
        <label className="fp-label">Registered mobile number</label>
        <div className="fp-mobile-row">
          <div className={`fp-input-wrap${otpSend ? " fp-input-wrap--disabled" : ""}`}>
            <span className="fp-prefix">+91</span>
            <input
              className="fp-input"
              type="tel"
              placeholder="Enter registered mobile number"
              autoComplete="tel"
              value={mob}
              onChange={(e) => handleNumber(e)}
              onKeyDown={verifyMobile}
              disabled={otpSend}
            />
          </div>

          {!otpSend ? (
            <button
              className="fp-send-otp-btn"
              type="button"
              onClick={() => resetPinFn()}
              disabled={loading || mob?.length !== 10}
            >
              {loading ? "Sending…" : "Send OTP"}
            </button>
          ) : (
            <button
              className={`fp-resend-btn${timer > 0 ? " fp-resend-btn--disabled" : ""}`}
              type="button"
              onClick={handleResendOtp}
              disabled={timer > 0}
            >
              {timer > 0 ? `Resend in ${timer}s` : "Resend OTP"}
            </button>
          )}
        </div>
      </div>

      {/* ── OTP verification card ── */}
      <div
        className={`fp-otp-card${
          otpSend
            ? otpVerified
              ? " fp-otp-card--verified"
              : " fp-otp-card--active"
            : " fp-otp-card--inactive"
        }`}
      >
        <div className="fp-otp-card-header">
          <div>
            <p className="fp-otp-card-title">OTP verification</p>
            <p className="fp-otp-card-desc">
              {otpVerified
                ? "Mobile number successfully verified."
                : "Enter the 6-digit code sent to the cashier mobile."}
            </p>
          </div>
          {otpVerified && (
            <div className="fp-verified-badge">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              Verified
            </div>
          )}
        </div>

        {/* OTP digit boxes */}
        <div className="fp-otp-boxes" onPaste={handleOtpPaste}>
          {Array.from({ length: 6 })?.map((_, i) => (
            <div key={i} className={`fp-otp-box${otpVerified ? " fp-otp-box--verified" : ""}`}>
              <input
                ref={(el) => (otpRefs.current[i] = el)}
                className="fp-otp-digit-input"
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={otp[i] || ""}
                onChange={(e) => handleOtpChange(i, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(i, e)}
                disabled={!otpSend}
                autoFocus={i === 0 && otpSend && !otpVerified}
              />
            </div>
          ))}
        </div>
      </div>

      {/* ── New PIN fields ── */}
      <div className={`fp-pin-grid${otpVerified ? "" : " fp-pin-grid--inactive"}`}>
        <div>
          <label className="fp-label">New 4-digit PIN</label>
          <input
            className="fp-pin-input"
            type="password"
            inputMode="numeric"
            placeholder="Create new PIN"
            value={pin}
            maxLength={4}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            disabled={!otpVerified}
          />
        </div>
        <div>
          <label className="fp-label">Confirm PIN</label>
          <input
            className="fp-pin-input"
            type="password"
            inputMode="numeric"
            placeholder="Re-enter new PIN"
            value={cPin}
            maxLength={4}
            onChange={(e) => setCPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            disabled={!otpVerified}
          />
        </div>
      </div>

      {/* ── Action buttons ── */}
      <div className="fp-actions">
        <button className="fp-cancel-btn" type="button" onClick={() => setForgotPin(false)}>
          Back to login
        </button>
        <button
          className="fp-reset-btn"
          type="button"
          onClick={saveResetPin}
          disabled={!isResetEnabled || resetPinLoader}
        >
          {resetPinLoader ? (
            <>
              <span className="fp-spinner"></span>
              Resetting...
            </>
          ) : (
            <>
              Reset PIN
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{ marginLeft: 8 }}
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};
